import * as React from "react";

import { SeriesColor } from "@/shared/domain/dashboards/DashboardDefinition";
import { Popover } from "@/shared/foundation/primitives";

import {
  PICKABLE_SERIES_COLORS,
  SERIES_COLOR_LABELS,
  seriesColorVars,
} from "../MetricChartCard/seriesColors";

import styles from "./SignalColorDot.module.css";

/** The colour a chart would draw a lone series in, given this choice — so the
 *  dot and the preview beside it can never show different things. */
function swatchColor(slot: SeriesColor | undefined): string {
  return `var(${seriesColorVars(1, slot)[0]})`;
}

export interface SignalColorDotProps {
  /** The signal's palette slot, or `undefined` for the theme's default accent. */
  value: SeriesColor | undefined;
  onChange: (color: SeriesColor) => void;
}

/**
 * The color of one signal, shown as a dot and changed by clicking it.
 *
 * The choices are slots of the series palette — the one a grouped chart
 * assigns from — so a picked colour and an assigned one share a vocabulary.
 * Only the first shade of each hue is offered (see `PICKABLE_SERIES_COLORS`).
 */
export function SignalColorDot({ value, onChange }: SignalColorDotProps) {
  const [open, setOpen] = React.useState(false);

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      aria-label="Signal color"
      trigger={
        <button
          type="button"
          className={styles.dot}
          style={{ background: swatchColor(value) }}
          aria-label={`Signal color: ${
            value === undefined ? "default" : SERIES_COLOR_LABELS[value]
          }`}
        />
      }
    >
      <div className={styles.swatches} data-testid="signal-color-swatches">
        {PICKABLE_SERIES_COLORS.map((slot) => (
          <button
            key={slot}
            type="button"
            className={styles.swatch}
            style={{ background: swatchColor(slot) }}
            aria-label={SERIES_COLOR_LABELS[slot]}
            aria-pressed={slot === value}
            onClick={() => {
              onChange(slot);
              setOpen(false);
            }}
          />
        ))}
      </div>
    </Popover>
  );
}
